// Numeric clinical rules, shared by vital signs (docs/plans/phase1-triage.md §2.3) and
// lab analytes (the lab slice). A rule applies to one *subject* (a vital key, an analyte
// code), an age band in whole days, optionally one sex and one context (such as fasting).
//
// Three kinds, all data configured by a clinical lead, none in code:
//   possible_limits  outside → refused ("not possible, check the value"); no override
//   reference_range  outside → flagged low or high (passive); never refused
//   critical_range   outside → critical (interruptive: acknowledge with a reason)
//
// Every bound is inclusive: a value equal to a bound is inside. Values are scaled
// integers in the subject's unit. Nothing here holds a clinical number.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const CLINICAL_RULE_KINDS = [
  'possible_limits',
  'reference_range',
  'critical_range',
] as const;
export type ClinicalRuleKind = (typeof CLINICAL_RULE_KINDS)[number];

export const CLINICAL_RULE_APPROVALS = ['provisional', 'approved', 'retired'] as const;
export type ClinicalRuleApproval = (typeof CLINICAL_RULE_APPROVALS)[number];

export const RULE_INTERPRETATIONS = [
  'normal',
  'low',
  'high',
  'critical_low',
  'critical_high',
  'no_range',
] as const;
export type RuleInterpretation = (typeof RULE_INTERPRETATIONS)[number];

/** Everything a rule has except its subject, whose field name each caller chooses. */
export interface ClinicalRuleCore {
  readonly id: string;
  readonly version: number;
  readonly kind: ClinicalRuleKind;
  /** Inclusive, in whole days. */
  readonly ageFromDays: number;
  /** Exclusive, in whole days; null means no upper bound. */
  readonly ageToDays: number | null;
  /** Scaled in the subject's unit; at least one of low and high is set. */
  readonly low: number | null;
  readonly high: number | null;
  /** A context such as `fasting`; null applies to any context. */
  readonly context: string | null;
  /** A sex code from the tenant's value set; null (or absent) applies to any sex. */
  readonly sex?: string | null;
  readonly approval: ClinicalRuleApproval;
  /** Where the numbers came from. Required. */
  readonly source: string;
}

/** Reads a rule's subject (`vitalKey`, `analyteCode`, …). */
export type SubjectOf<T> = (rule: T) => string;

export interface ClinicalRuleQuery {
  readonly subject: string;
  readonly kind: ClinicalRuleKind;
  /** Age in whole days at the clinical time; null when no date of birth is recorded. */
  readonly ageDays: number | null;
  readonly context: string | null;
  /** The patient's sex code; null when unknown, which matches only rules for any sex. */
  readonly sex?: string | null;
}

function bandContains(rule: ClinicalRuleCore, ageDays: number | null): boolean {
  // Without an age, only a rule for every age applies.
  if (ageDays === null) return rule.ageFromDays === 0 && rule.ageToDays === null;
  return ageDays >= rule.ageFromDays && (rule.ageToDays === null || ageDays < rule.ageToDays);
}

/** 0–3: how specific a matching rule is. Context outranks sex. */
function specificity(rule: ClinicalRuleCore): number {
  return (rule.context === null ? 0 : 2) + ((rule.sex ?? null) === null ? 0 : 1);
}

export type RuleSelectionErrorCode = 'clinical_rule.overlap';

/**
 * The one rule of a kind that applies to this subject, age, context and sex, or null when
 * none does ("No range set"). The most specific wins: a rule for the value's own context
 * over one for any context, then one for the patient's sex over one for any sex. Retired
 * rules never apply. Two applicable rules at the same specificity is a configuration
 * error that `validateClinicalRules` prevents; it is reported, never resolved by guessing.
 */
export function selectClinicalRule<T extends ClinicalRuleCore>(
  rules: readonly T[],
  query: ClinicalRuleQuery,
  subjectOf: SubjectOf<T>,
): Result<T | null, RuleSelectionErrorCode> {
  const sex = query.sex ?? null;
  const applicable = rules.filter(
    (r) =>
      r.approval !== 'retired' &&
      subjectOf(r) === query.subject &&
      r.kind === query.kind &&
      bandContains(r, query.ageDays) &&
      (r.context === null || r.context === query.context) &&
      ((r.sex ?? null) === null || r.sex === sex),
  );
  if (applicable.length === 0) return ok(null);
  const best = Math.max(...applicable.map(specificity));
  const chosen = applicable.filter((r) => specificity(r) === best);
  if (chosen.length > 1) return err('clinical_rule.overlap');
  return ok(chosen[0] ?? null);
}

/** Whether a value lies outside the rule's bounds, and on which side. */
export function sideOf(scaled: number, rule: ClinicalRuleCore): 'low' | 'high' | null {
  if (rule.low !== null && scaled < rule.low) return 'low';
  if (rule.high !== null && scaled > rule.high) return 'high';
  return null;
}

export interface OutsideLimits<T> {
  readonly ok: false;
  readonly code: 'clinical_rule.not_possible';
  readonly rule: T;
  readonly side: 'low' | 'high';
}

/**
 * Refuses a value outside the possible limits. With no limits configured the value is
 * accepted: a configuration gap that Settings reports, not a reason to refuse everything.
 */
export function checkWithinLimits<T extends ClinicalRuleCore>(
  scaled: number,
  limits: T | null,
): { readonly ok: true } | OutsideLimits<T> {
  if (limits === null) return { ok: true };
  if (limits.kind !== 'possible_limits') {
    throw new Error('checkWithinLimits needs a possible_limits rule');
  }
  const side = sideOf(scaled, limits);
  if (side === null) return { ok: true };
  return { ok: false, code: 'clinical_rule.not_possible', rule: limits, side };
}

export interface RuleInterpretationResult<T> {
  readonly interpretation: RuleInterpretation;
  /** The rule that decided: the critical rule if critical, else the reference rule. */
  readonly rule: T | null;
}

/**
 * Flags a possible value: critical first, then the reference range. With no reference
 * range the value is `no_range`, unless it is critical: a critical threshold stands alone.
 */
export function interpretValue<T extends ClinicalRuleCore>(
  scaled: number,
  reference: T | null,
  critical: T | null,
): RuleInterpretationResult<T> {
  if (reference !== null && reference.kind !== 'reference_range') {
    throw new Error('interpretValue needs a reference_range rule');
  }
  if (critical !== null && critical.kind !== 'critical_range') {
    throw new Error('interpretValue needs a critical_range rule');
  }
  if (critical !== null) {
    const side = sideOf(scaled, critical);
    if (side === 'low') return { interpretation: 'critical_low', rule: critical };
    if (side === 'high') return { interpretation: 'critical_high', rule: critical };
  }
  if (reference === null) return { interpretation: 'no_range', rule: null };
  return { interpretation: sideOf(scaled, reference) ?? 'normal', rule: reference };
}

export function isCritical(value: RuleInterpretation): boolean {
  return value === 'critical_low' || value === 'critical_high';
}

export interface ClinicalAssessment<T> {
  readonly interpretation: RuleInterpretation;
  readonly rule: T | null;
  /** True when the rule that decided the flag is not yet approved. */
  readonly provisional: boolean;
}

/** The whole judgement for one value: refused if not possible, otherwise flagged. */
export function assessValue<T extends ClinicalRuleCore>(
  rules: readonly T[],
  input: Omit<ClinicalRuleQuery, 'kind'> & { readonly scaled: number },
  subjectOf: SubjectOf<T>,
):
  | { readonly ok: true; readonly value: ClinicalAssessment<T> }
  | OutsideLimits<T>
  | { readonly ok: false; readonly code: RuleSelectionErrorCode } {
  const query = (kind: ClinicalRuleKind) =>
    selectClinicalRule(rules, { ...input, kind }, subjectOf);
  const limits = query('possible_limits');
  if (!limits.ok) return limits;
  const within = checkWithinLimits(input.scaled, limits.value);
  if (!within.ok) return within;
  const reference = query('reference_range');
  if (!reference.ok) return reference;
  const critical = query('critical_range');
  if (!critical.ok) return critical;
  const judged = interpretValue(input.scaled, reference.value, critical.value);
  return {
    ok: true,
    value: { ...judged, provisional: judged.rule?.approval === 'provisional' },
  };
}

// --- Validating a rule set --------------------------------------------------------------

export type RuleIssueCode =
  | 'rule.no_bounds'
  | 'rule.low_above_high'
  | 'rule.bad_age_band'
  | 'rule.context_not_allowed'
  | 'rule.missing_source'
  | 'rule.overlap'
  | 'rule.outside_possible'
  | 'rule.critical_inside_reference';

export const RULE_ISSUES: Record<RuleIssueCode, string> = {
  'rule.no_bounds': 'A rule needs a low value, a high value or both',
  'rule.low_above_high': 'The low value is above the high value',
  'rule.bad_age_band': 'The age band must start at 0 or later and end after it starts',
  'rule.context_not_allowed': 'This measurement does not take that context',
  'rule.missing_source': 'Say where the numbers come from',
  'rule.overlap': 'Two rules of the same kind cover the same ages',
  'rule.outside_possible': 'A range goes beyond the possible limits for the same ages',
  'rule.critical_inside_reference': 'A critical threshold lies inside the reference range',
};

export interface RuleIssue {
  readonly code: RuleIssueCode;
  readonly ruleIds: readonly string[];
}

function bandsOverlap(a: ClinicalRuleCore, b: ClinicalRuleCore): boolean {
  const aTo = a.ageToDays ?? Number.POSITIVE_INFINITY;
  const bTo = b.ageToDays ?? Number.POSITIVE_INFINITY;
  return a.ageFromDays < bTo && b.ageFromDays < aTo;
}

const sexOf = (r: ClinicalRuleCore) => r.sex ?? null;

/** Whether two rules could both apply to one value at the same specificity. */
function competing<T extends ClinicalRuleCore>(a: T, b: T, subjectOf: SubjectOf<T>): boolean {
  return (
    subjectOf(a) === subjectOf(b) &&
    a.kind === b.kind &&
    a.context === b.context &&
    sexOf(a) === sexOf(b) &&
    bandsOverlap(a, b)
  );
}

/** Whether two rules, of any kinds, can apply to the same value. */
function coApplicable<T extends ClinicalRuleCore>(a: T, b: T, subjectOf: SubjectOf<T>): boolean {
  return (
    subjectOf(a) === subjectOf(b) &&
    (a.context === null || b.context === null || a.context === b.context) &&
    (sexOf(a) === null || sexOf(b) === null || sexOf(a) === sexOf(b)) &&
    bandsOverlap(a, b)
  );
}

/**
 * Every problem with a set of rules, before it is saved. Retired rules are ignored. Gaps
 * between age bands are allowed: they read as "No range set for this age".
 */
export function validateClinicalRules<T extends ClinicalRuleCore>(
  rules: readonly T[],
  options: {
    subjectOf: SubjectOf<T>;
    /** Whether the subject takes this context; null (any context) is always allowed. */
    contextAllowed: (subject: string, context: string) => boolean;
  },
): RuleIssue[] {
  const { subjectOf } = options;
  const live = rules.filter((r) => r.approval !== 'retired');
  const issues: RuleIssue[] = [];
  for (const rule of live) {
    const one = (code: RuleIssueCode) => issues.push({ code, ruleIds: [rule.id] });
    if (rule.low === null && rule.high === null) one('rule.no_bounds');
    if (rule.low !== null && rule.high !== null && rule.low > rule.high) one('rule.low_above_high');
    if (
      !Number.isSafeInteger(rule.ageFromDays) ||
      rule.ageFromDays < 0 ||
      (rule.ageToDays !== null &&
        (!Number.isSafeInteger(rule.ageToDays) || rule.ageToDays <= rule.ageFromDays))
    ) {
      one('rule.bad_age_band');
    }
    if (rule.context !== null && !options.contextAllowed(subjectOf(rule), rule.context)) {
      one('rule.context_not_allowed');
    }
    if (rule.source.trim() === '') one('rule.missing_source');
  }
  for (let i = 0; i < live.length; i += 1) {
    for (let j = i + 1; j < live.length; j += 1) {
      const a = live[i];
      const b = live[j];
      if (a === undefined || b === undefined) continue;
      if (competing(a, b, subjectOf)) issues.push({ code: 'rule.overlap', ruleIds: [a.id, b.id] });
      if (!coApplicable(a, b, subjectOf)) continue;
      const [limits, other] =
        a.kind === 'possible_limits' ? [a, b] : b.kind === 'possible_limits' ? [b, a] : [];
      if (limits !== undefined && other !== undefined && other.kind !== 'possible_limits') {
        const belowLow = limits.low !== null && other.low !== null && other.low < limits.low;
        const aboveHigh = limits.high !== null && other.high !== null && other.high > limits.high;
        if (belowLow || aboveHigh) {
          issues.push({ code: 'rule.outside_possible', ruleIds: [limits.id, other.id] });
        }
      }
      const reference = [a, b].find((r) => r.kind === 'reference_range');
      const critical = [a, b].find((r) => r.kind === 'critical_range');
      if (reference !== undefined && critical !== undefined) {
        // A critical low must not be above the reference low, nor a critical high below
        // the reference high: a critical value can't also be a normal one.
        const lowInside =
          critical.low !== null && reference.low !== null && critical.low > reference.low;
        const highInside =
          critical.high !== null && reference.high !== null && critical.high < reference.high;
        if (lowInside || highInside) {
          issues.push({
            code: 'rule.critical_inside_reference',
            ruleIds: [reference.id, critical.id],
          });
        }
      }
    }
  }
  return issues;
}
